/**
 * transformStages.ts — the AI-design progress stages shown while a redesign runs.
 *
 * Labels and cadence come from the Deckorate add-in mockup's `transformRun`
 * animation. They are presentational: the backend reports a single stage over
 * SSE ("Redesign Engine"), so these describe the pipeline as a whole rather than
 * tracking server-side steps one-for-one.
 *
 * A real redesign takes far longer than the animation, so the sequence holds on
 * the final stage until the work actually finishes — it never claims completion
 * the run hasn't reached. See `useStagedProgress`.
 */

export const TRANSFORM_STAGES: readonly string[] = [
  "Analyzing slide layout…",
  "Drafting new layout…",
  "Composing slide shapes…",
  "Building presentation file…",
  "Inserting redesigned slide…",
  "Polishing layout…",
] as const;

/** Milliseconds each stage is displayed before advancing, per the mockup. */
export const TRANSFORM_STAGE_INTERVAL_MS = 700;

/** Heading shown above the stage list while a redesign is running. */
export const TRANSFORM_PROGRESS_TITLE = "Transforming slide…";

/* ── Template mode ──────────────────────────────────────────────── */

/**
 * Stages while the slide is classified and ranked against the template catalog
 * (`POST /slides/transform-retrieve`).
 *
 * Unlike the freehand stages above these are close to real: the retrieve step
 * genuinely reads the slide, classifies it and scores the catalog, with no LLM
 * call, so it returns in well under a second.
 */
export const TEMPLATE_MATCH_STAGES: readonly string[] = [
  "Reading your slide…",
  "Looking at your slide…",
  "Analyzing slide meaning…",
  "Finding matching templates…",
] as const;

export const TEMPLATE_MATCH_STAGE_INTERVAL_MS = 650;

export const TEMPLATE_MATCH_PROGRESS_TITLE = "Analyzing your slide…";

/**
 * Stages while the picked templates are fetched, filled and inserted.
 *
 * These DO track the real pipeline — see `useTemplateTransform`, which advances
 * them as each step completes rather than on a timer, because the steps have
 * very different durations (the content fit is an LLM call; the rest is local).
 * The fit stage's label depends on the fill mode, so it is supplied separately.
 *
 * The mockup lists the fit before the fetch. That order can't happen: fitting
 * needs the template's slot capacities, which are read out of the template file,
 * so the fetch and parse must come first. The mockup's labels are kept exactly;
 * only their order is corrected, so the list still reports the real step.
 */
export const TEMPLATE_APPLY_STAGES: readonly string[] = [
  "Picking best matches…",
  "Fetching template…",
  "Parsing slide structure…",
  "Fitting content to template slots…",
  "Inserting your content…",
  "Packaging the slide…",
  "Adding to your deck…",
] as const;

/** Index of the stage whose label changes with the fill mode. */
export const TEMPLATE_APPLY_FIT_STAGE_INDEX = 3;

/** The fit stage's label for each fill mode, per the mockup. */
export const TEMPLATE_FIT_STAGE_LABEL = {
  generic: "AI-fitting content to template slots…",
  preserve: "Mapping content to template…",
} as const;

export const TEMPLATE_APPLY_PROGRESS_TITLE = "Applying template…";
